/**
 * The Panel's contract validators, one schema at a time. The build generates each validator as code
 * (`scripts/contract-validators.mjs`, Ajv standalone): no schema registry and no Ajv compiler is downloaded, and no
 * validator is compiled in the browser. Each validator is its own small file, asked for the first time an operation
 * needs it — beside the request it validates, so it travels while the request does — and kept for the session.
 */

export type ContractSchemaKind = 'request' | 'response';
export type ContractValidate = (value: unknown) => boolean;

/** Where the validators come from: the build's generated files, or the compiled registry in the specs. */
interface ContractValidatorSource {
  readonly loadContractValidator: (kind: ContractSchemaKind, name: string) => Promise<ContractValidate>;
  readonly peekContractValidator?: (kind: ContractSchemaKind, name: string) => ContractValidate | undefined;
}

const SCHEMA_MISSING = 'identity_contract_schema_missing';
const loaded = new Map<string, ContractValidate>();
const loading = new Map<string, Promise<ContractValidate>>();
let installed: ContractValidatorSource | null = null;
let generated: Promise<ContractValidatorSource> | null = null;

/** The specs install the compiled registry, so every validator is at hand synchronously. */
export function installContractValidatorSource(source: ContractValidatorSource): void {
  installed = source;
}

/** A validator already at hand, or undefined. */
export function contractValidator(kind: ContractSchemaKind, name: string): ContractValidate | undefined {
  return loaded.get(`${kind}:${name}`) ?? installed?.peekContractValidator?.(kind, name);
}

/** Whether a failure to load a validator is the schema's absence (a bug), not the network. */
export const contractSchemaMissing = (error: unknown): boolean => error instanceof Error && error.message === SCHEMA_MISSING;

/** Loads one validator; a failed load is forgotten, so the next request retries it. */
export function loadContractValidator(kind: ContractSchemaKind, name: string): Promise<ContractValidate> {
  const key = `${kind}:${name}`;
  const ready = contractValidator(kind, name);
  if (ready !== undefined) return Promise.resolve(ready);
  let pending = loading.get(key);
  if (pending === undefined) {
    const source = installed !== null ? Promise.resolve(installed)
      : generated ??= import('virtual:contract-validators').catch((error: unknown) => { generated = null; throw error; });
    pending = source.then((from) => from.loadContractValidator(kind, name)).then(
      (validate) => { loaded.set(key, validate); loading.delete(key); return validate; },
      (error: unknown) => { loading.delete(key); throw error; },
    );
    loading.set(key, pending);
  }
  return pending;
}

/** Warms validators that will be needed soon; a failure is left for the request that needs them. */
export function preloadContractValidators(kind: ContractSchemaKind, names: Iterable<string>): void {
  // The compiled registry has every validator at hand already.
  if (installed !== null) return;
  for (const name of names) {
    if (contractValidator(kind, name) === undefined) void loadContractValidator(kind, name).catch(() => undefined);
  }
}
